import { Fragment, createContext, useContext, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CSSProperties, ReactNode } from 'react';
import { Icon, Badge, Avatar, ProjectID, TONE, markColor, type ToneName } from './primitives';
import { Card, Eyebrow, useInSurface } from './card';
import { useBelow } from '../lib/useMediaQuery';
import { dayMarkStyle } from './monthGrid';
import type { HolidayKind } from '../lib/holidays';
import { LEVELS, type SkillLevel } from '../lib/capacity';

// A skill at its level: the name, the level's colour as a mark, and the
// level's name, so the colour is never the only cue (owner, 2026-10-13). It was
// a skill with a % in one tone; skills are four levels now (lib/capacity).
export const SkillLevelChip = ({ name, level }: { name: string; level: SkillLevel }) => {
  const l = LEVELS[level] ?? LEVELS[2];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 8px 2px 6px',
        borderRadius: 999,
        background: 'var(--hf-surface-2)',
        border: '1px solid var(--hf-rule-2)',
        fontSize: 'var(--hf-text-2xs)',
        color: 'var(--hf-ink)',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        aria-hidden
        style={{ width: 8, height: 8, borderRadius: 2, background: l.color, flexShrink: 0 }}
      />
      {name}
      <span style={{ color: 'var(--hf-ink-3)' }}>· {l.label}</span>
    </span>
  );
};

export const Sparkline = ({
  data = [],
  color = 'var(--hf-accent)',
  w = 120,
  h = 36,
}: {
  data?: number[];
  color?: string;
  w?: number;
  h?: number;
}) => {
  const max = Math.max(...data),
    min = Math.min(...data),
    range = max - min || 1;
  const pts = data
    .map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * (h - 4) - 2}`)
    .join(' ');
  const area = `M0,${h} L${pts.split(' ').join(' L')} L${w},${h} Z`;
  // One id per line: a shared "sparkg" filled every sparkline on the page with
  // the first one's colour.
  const gid = useId();
  return (
    <svg width={w} height={h} style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={w}
        cy={h - ((data[data.length - 1] - min) / range) * (h - 4) - 2}
        r="2.5"
        fill={color}
      />
    </svg>
  );
};

type KpiVariant = 'spark' | 'bar' | 'ring';
type Trend = 'up' | 'down' | 'neutral';

// A figure with its label. Two different things used to share the `delta` slot
// in the header: a trend ("+3 vs last week") and a status note ("org day · 40h
// this week", "provisional"). The header couldn't wrap, so at 1024px the label
// and the note ran into each other, and a note got a flat dash in front of it
// that read as a minus sign (TS-17). Now `delta` is only a trend — ▲/▼ and the
// status tone, no glyph when neutral — and both have a line of their own under
// the value: the change first, then what the figure is. Under the figure, a
// change can say what it is measured against ("▲ 5 pts vs previous 28 days")
// without crowding the label. It reads in the UI face, not mono: it is a
// sentence with a number in it (critique 2026-10-06, mono carrying prose).
//
// On the page a tile is a glass card. Inside another surface (a Card, a
// Dialog, a hand-built header in <Surface>) it is a bare figure — label, value,
// note, no box — one numeral step smaller, since a tile in a card was a card in
// a card (UI-CONVENTIONS §8). That retired MeStat, /me's private copy.
export const KpiTile = ({
  label,
  value,
  unit,
  delta,
  trend = 'up',
  note,
  data,
  tone = 'accent',
  variant = 'spark',
  highlightIndex,
  size = 'md',
  deltaAt,
}: {
  label: string;
  /**
   * `null` while loading or when there is nothing to measure: the "—" stub, the
   * same honesty as a null in `data`. Never pass a placeholder 0 (TS-14, ME-14).
   */
  value: ReactNode;
  unit?: string;
  /** A change against another period. Coloured and pointed by `trend`. */
  delta?: string | null;
  trend?: Trend;
  /** What the figure is, or is not yet ("org day", "provisional"). Never coloured. */
  note?: ReactNode;
  /** 'sm' for a secondary row or a phone: less padding, a smaller numeral (HRA-13). */
  size?: 'md' | 'sm';
  /**
   * `null` means "not measured yet" and renders as the 2px stub — it is not 0.
   * Callers used to floor a missing value to 0.1 to dodge a divide-by-zero in
   * the bar variant, which made an all-missing series render as a row of
   * FULL-HEIGHT bars: the picture of "no data" was a maxed-out chart.
   */
  data?: (number | null)[];
  tone?: ToneName;
  variant?: KpiVariant;
  highlightIndex?: number; // bar variant: which bar gets the accent (default: last)
  /**
   * 'head': the design's place for a change (domain.jsx KpiTile) — mono, top
   * right, beside the label. The arrow follows the number's sign and the colour
   * follows `trend` (good or bad), so "−8 bench hours" can be good news. A
   * phone's strip keeps it under the figure, where the label can't crowd it.
   */
  deltaAt?: 'head';
}) => {
  const tc = markColor(tone);
  // Bars scale against the largest REAL value, computed once rather than per
  // bar. `Math.max(0, …)` over an all-null series gives 0, which the height
  // below reads as "nothing to scale" instead of dividing by it.
  const barMax = Math.max(0, ...(data ?? []).filter((v): v is number => v != null));
  // The delta is text: success/danger's -text, not the bare hue (1.3–2.4:1 in light).
  const deltaColor =
    trend === 'up' ? TONE.success.fg : trend === 'down' ? TONE.danger.fg : 'var(--hf-ink-3)';
  const sm = size === 'sm';
  const bare = useInSurface();
  const strip = useContext(InStrip);
  const head = deltaAt === 'head' && !strip;
  // md 32 · sm 24 on the page; a step down inside a surface; 18 in a phone's strip.
  const numeral = strip
    ? 'var(--hf-text-xl)'
    : ['var(--hf-text-3xl)', 'var(--hf-text-2xl)', 'var(--hf-text-xl)'][
        (sm ? 1 : 0) + (bare ? 1 : 0)
      ];
  return (
    <div
      className={bare ? undefined : 'hf-glass'}
      style={{
        borderRadius: 'var(--hf-radius-card)',
        padding: bare ? 0 : sm ? 'var(--hf-space-3)' : 'var(--hf-pad-card)',
        // In a phone's strip a tile is three rows of the strip's grid (label,
        // figure, the rest), so two tiles side by side share their label row:
        // a label that wrapped no longer dropped its neighbour's figure 20px
        // (critique review 2026-10-08).
        ...(strip
          ? { display: 'grid', gridRow: 'span 3', gridTemplateRows: 'subgrid' }
          : { display: 'flex', flexDirection: 'column' }),
        gap: strip ? 'var(--hf-space-1)' : 'var(--hf-space-2)',
        // As tall as what it holds. A 112px floor left a bare number over ~50px
        // of nothing on the People dashboard, four tiles across (critique
        // 2026-10-05 #3). A row of tiles still lines up: KpiGrid's items stretch.
        // A grid item's floor is its content's width: a long figure pushed the
        // tile past a 320px phone's edge instead of wrapping.
        minWidth: 0,
      }}
    >
      {/* A one-line label sits on its figure, not at the top of a taller row. */}
      {head ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 'var(--hf-space-2)',
          }}
        >
          <Eyebrow>{label}</Eyebrow>
          {delta != null && (
            <span
              style={{
                fontSize: 'var(--hf-text-2xs)',
                fontFamily: 'var(--hf-font-mono)',
                color: deltaColor,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                whiteSpace: 'nowrap',
              }}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                aria-hidden="true"
              >
                <path
                  d={
                    /^[-−]/.test(delta)
                      ? 'M2 3l3 4 3-4'
                      : /^[+1-9]/.test(delta)
                        ? 'M2 7l3-4 3 4'
                        : 'M2 5h6'
                  }
                />
              </svg>
              {delta}
            </span>
          )}
        </div>
      ) : (
        <div style={strip ? { alignSelf: 'end' } : undefined}>
          <Eyebrow>{label}</Eyebrow>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--hf-space-1)' }}>
        <span
          style={{
            fontFamily: 'var(--hf-font-display)',
            fontSize: numeral,
            fontWeight: 'var(--hf-weight-regular)',
            color: value == null ? 'var(--hf-ink-3)' : 'var(--hf-ink)',
            letterSpacing: 'var(--hf-track-display)',
            lineHeight: 'var(--hf-leading-none)',
          }}
        >
          {value == null ? (
            <>
              <span aria-hidden="true">—</span>
              <span className="hf-sr-only">No figure yet</span>
            </>
          ) : (
            value
          )}
        </span>
        {unit && value != null && (
          <span
            style={{
              fontSize: 'var(--hf-text-sm)',
              color: 'var(--hf-ink-3)',
              fontFamily: 'var(--hf-font-mono)',
            }}
          >
            {unit}
          </span>
        )}
      </div>
      {/* The strip's third row. On the page it draws no box of its own, so the
          tile's column lays these out as it always did. */}
      <div
        style={
          strip
            ? { display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-1)' }
            : { display: 'contents' }
        }
      >
        {delta != null && !head && (
          <span
            style={{
              fontSize: 'var(--hf-text-xs)',
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 'var(--hf-leading-snug)',
              color: deltaColor,
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--hf-space-1)',
            }}
          >
            {/* Shape as well as colour: up and down must survive colour blindness. */}
            {trend !== 'neutral' && (
              <svg width="8" height="8" viewBox="0 0 10 10" aria-hidden="true">
                <path d={trend === 'up' ? 'M5 1l4 8H1z' : 'M5 9l4-8H1z'} fill="currentColor" />
              </svg>
            )}
            {delta}
          </span>
        )}
        {note != null && (
          <div
            style={{
              fontSize: 'var(--hf-text-xs)',
              color: 'var(--hf-ink-3)',
              lineHeight: 'var(--hf-leading-snug)',
            }}
          >
            {note}
          </div>
        )}
        {variant === 'spark' && data && (
          <div style={{ marginTop: 'auto', marginLeft: -4, marginRight: -4, marginBottom: -4 }}>
            {/* A sparkline is a continuous line — it has no way to draw a gap, so
              a missing point reads as 0 here. Only the bar variant distinguishes
              "not measured" from "zero". */}
            <Sparkline data={data.map((v) => v ?? 0)} color={tc} w={220} h={32} />
          </div>
        )}
        {variant === 'bar' && data && (
          <div
            style={{
              display: 'flex',
              gap: 2,
              alignItems: 'flex-end',
              height: 32,
              marginTop: 'auto',
            }}
          >
            {data.map((v, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  // A missing value, and a series with nothing in it, both fall to
                  // the minHeight stub. Neither can reach `v / 0` → `NaN%`.
                  height: v == null || barMax <= 0 ? 0 : `${(v / barMax) * 100}%`,
                  background: i === (highlightIndex ?? data.length - 1) ? tc : 'var(--hf-rule-3)',
                  borderRadius: 1,
                  minHeight: 2,
                }}
              />
            ))}
          </div>
        )}
        {variant === 'ring' && data && (
          <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="32" height="32" viewBox="0 0 32 32">
              <circle
                cx="16"
                cy="16"
                r="12"
                fill="none"
                stroke="var(--hf-rule-2)"
                strokeWidth="3"
              />
              <circle
                cx="16"
                cy="16"
                r="12"
                fill="none"
                stroke={tc}
                strokeWidth="3"
                // data is [value, total] — arc = value/total of the 75.4 circumference
                // (2πr, r=12), clamped so an over-target value can't wrap the ring.
                strokeDasharray={`${Math.min(1, (data[0] || 0) / (data[1] || 100)) * 75.4} 100`}
                strokeLinecap="round"
                transform="rotate(-90 16 16)"
              />
            </svg>
            {/* "of 100" under a percentage says nothing (TS-17). */}
            {(data[1] || 100) !== 100 && (
              <span
                style={{
                  fontSize: 'var(--hf-text-2xs)',
                  color: 'var(--hf-ink-3)',
                  fontFamily: 'var(--hf-font-mono)',
                }}
              >
                of {data[1]}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// A row of KpiTiles that reflows: fixed repeat(4,1fr) rows clipped at 1024px and
// had no phone layout (ORG-17, TS-04). The auto-fit pattern PeopleHR's metric
// row already used.
//
// Below sm it is one strip, not a stack of boxes: at 320px four tiles were four
// glass boxes filling the first screen, one number each (critique 2026-10-06
// #1). The strip is one card (or none, inside a surface), and each tile in it
// is a bare label, figure, change and note, two to a row.
const InStrip = createContext(false);
export const KpiGrid = ({ children }: { children?: ReactNode }) => {
  const strip = useBelow('sm');
  const nested = useInSurface();
  if (strip) {
    const row = (
      <div
        style={{
          display: 'grid',
          // Two to a row, down to a 320px screen's card.
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 7rem), 1fr))',
          gap: 'var(--hf-space-4)',
        }}
      >
        <InStrip.Provider value>{children}</InStrip.Provider>
      </div>
    );
    return nested ? row : <Card>{row}</Card>;
  }
  return (
    <div
      style={{
        display: 'grid',
        // min(): a container under 150px (a 320px phone's card) gets one full
        // column, not a 150px track past its edge.
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))',
        gap: 'var(--hf-space-3)',
      }}
    >
      {children}
    </div>
  );
};

// With onSelect, each stage is a button that moves the project there (the
// project page, for whoever may: set_project_stage). Without it, a picture.
export const StagePipeline = ({
  stages = [],
  current = 0,
  onSelect,
  busy,
}: {
  stages?: string[];
  current?: number;
  onSelect?: (index: number) => void;
  /** A move is saving: the buttons wait. */
  busy?: boolean;
}) => (
  <div
    role={onSelect ? 'group' : undefined}
    aria-label={onSelect ? 'Stage' : undefined}
    aria-busy={busy || undefined}
    style={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}
  >
    {stages.map((s, i) => {
      const done = i < current,
        active = i === current;
      const Step = onSelect ? 'button' : 'div';
      return (
        <Fragment key={i}>
          <Step
            {...(onSelect
              ? {
                  type: 'button' as const,
                  onClick: () => onSelect(i),
                  disabled: busy,
                  'aria-pressed': active,
                  title: active ? `${s}: the current stage` : `Move to ${s}`,
                  className: 'hf-hit',
                }
              : {})}
            style={{
              font: 'inherit',
              textAlign: 'left',
              cursor: onSelect ? (busy ? 'progress' : 'pointer') : undefined,
              padding: '6px 10px',
              borderRadius: 4,
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: active
                ? 'var(--hf-accent-wash)'
                : done
                  ? 'var(--hf-surface-2)'
                  : 'transparent',
              border: `1px solid ${active ? 'var(--hf-accent-line)' : 'var(--hf-rule)'}`,
              fontSize: 11.5,
              color: active
                ? 'var(--hf-accent-text)'
                : done
                  ? 'var(--hf-ink-2)'
                  : 'var(--hf-ink-3)',
            }}
          >
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 999,
                border: `1px solid ${active ? 'var(--hf-accent)' : done ? 'var(--hf-sage)' : 'var(--hf-rule-3)'}`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {done && (
                <svg
                  width="8"
                  height="8"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--hf-sage)"
                  strokeWidth="3"
                >
                  <path d="M5 12l4 4 10-10" />
                </svg>
              )}
              {active && (
                <span
                  style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--hf-accent)' }}
                />
              )}
            </span>
            <span>{s}</span>
          </Step>
          {i < stages.length - 1 && (
            <div style={{ width: 4, height: 1, background: 'var(--hf-rule-2)' }} />
          )}
        </Fragment>
      );
    })}
  </div>
);

export const TimerPushButton = ({
  running,
  task,
  projectId,
  time,
  onToggle,
}: {
  running?: boolean;
  task: string;
  projectId: string;
  time: string;
  onToggle?: () => void;
}) => (
  <div
    className="hf-glass"
    style={{
      padding: 16,
      borderRadius: 14,
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      border: running ? '1px solid var(--hf-accent-line)' : '1px solid var(--hf-rule-2)',
      boxShadow: running
        ? '0 0 0 1px var(--hf-accent-line), 0 12px 40px -12px var(--hf-accent-glow)'
        : undefined,
    }}
  >
    <button
      onClick={onToggle}
      style={{
        width: 52,
        height: 52,
        borderRadius: 999,
        flexShrink: 0,
        background: running ? 'var(--hf-accent-gradient)' : 'var(--hf-surface-3)',
        border: running ? 'none' : '1px solid var(--hf-rule-3)',
        // White only on the accent fill; on surface-3 it vanished in light theme.
        color: running ? 'var(--hf-accent-ink)' : 'var(--hf-ink)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: running
          ? '0 0 0 6px var(--hf-accent-wash), 0 8px 24px -8px var(--hf-accent-glow)'
          : undefined,
      }}
    >
      {running ? (
        <Icon name="pause" size={18} stroke={2.5} />
      ) : (
        <Icon name="play" size={18} stroke={2.5} style={{ marginLeft: 2 }} />
      )}
    </button>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <ProjectID id={projectId} />
        {running && (
          <Badge tone="accent" dot>
            Live
          </Badge>
        )}
      </div>
      <div
        style={{
          fontSize: 14,
          color: 'var(--hf-ink)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {task}
      </div>
    </div>
    <div
      style={{
        fontFamily: 'var(--hf-font-mono)',
        fontSize: 22,
        color: running ? 'var(--hf-accent-text)' : 'var(--hf-ink)',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {time}
    </div>
  </div>
);

export const ActiveTimerPill = ({
  task,
  time,
}: {
  task: string;
  time: string;
  onStop?: () => void;
}) => (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 10,
      padding: '6px 6px 6px 12px',
      borderRadius: 999,
      background: 'var(--hf-accent-wash)',
      border: '1px solid var(--hf-accent-line)',
      boxShadow: '0 0 20px -4px var(--hf-accent-glow)',
    }}
  >
    <span
      className="hf-pulse-dot"
      style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--hf-accent)' }}
    />
    <span
      style={{
        fontSize: 11.5,
        color: 'var(--hf-ink-2)',
        maxWidth: 140,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {task}
    </span>
    <span
      style={{
        fontFamily: 'var(--hf-font-mono)',
        fontSize: 12,
        color: 'var(--hf-accent-text)',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {time}
    </span>
    <button
      style={{
        width: 22,
        height: 22,
        borderRadius: 999,
        background: 'var(--hf-accent-wash)',
        border: 'none',
        color: 'var(--hf-accent-text)',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon name="stop" size={9} />
    </button>
  </div>
);

// ── Gantt row ─────────────────────────────────────────────────────────────────
// One project on the Schedule, as the Claude Design draws it (screens2.jsx
// GanttScreen). Left: one dot in its colour (glowing while it runs), the name
// (greyed and struck through once finished) and the code, with an at-risk or
// blocked flag beside it. Right, the track: a see-through, outlined bar from
// `bar.from` to `bar.to` (fractions of the window) carrying the lead's avatar
// and first name, or a `note` where a date is missing, which is never guessed.
// `markers` are diamonds at fractions of the window, the project's work items'
// due dates, each named for a screen reader. The screen draws the grid and
// today into `background`, behind every row's track.
// Keep the 240px label column in step with the screen's header.
const FLAG: Record<'at-risk' | 'blocked', CSSProperties> = {
  'at-risk': {
    background: 'var(--hf-amber-wash)',
    color: 'var(--hf-amber-text)',
    border: '1px solid var(--hf-amber-line)',
  },
  blocked: {
    background: 'var(--hf-rose-wash)',
    color: 'var(--hf-rose-text)',
    border: '1px solid var(--hf-rose-line)',
  },
};
export const GanttRow = ({
  name,
  code,
  tone,
  done = false,
  flag = null,
  lead = null,
  bar = null,
  note,
  markers = [],
  background,
  href,
}: {
  name: string;
  code: string;
  tone: string;
  done?: boolean;
  flag?: 'at-risk' | 'blocked' | null;
  lead?: string | null;
  bar?: { from: number; to: number } | null;
  note?: string;
  markers?: { at: number; label: string }[];
  background?: ReactNode;
  href?: string;
}) => {
  const [hot, setHot] = useState(false);
  // `tone` is a DB value (projects.tone), so markColor's fallback matters here.
  const hue = markColor(tone);
  const mix = (pct: number) => `color-mix(in srgb, ${hue} ${pct}%, transparent)`;
  // The design's narrowest bar is 1.5% of the window.
  const width = bar ? Math.max(bar.to - bar.from, 0.015) : 0;
  const style: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '240px 1fr',
    alignItems: 'center',
    height: 55,
    boxSizing: 'border-box',
    padding: '5px 16px',
    borderBottom: '1px solid var(--hf-rule)',
    color: 'inherit',
    textDecoration: 'none',
  };
  const body = (
    <>
      <span
        style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, paddingRight: 12 }}
      >
        <span
          aria-hidden
          style={{
            width: 5,
            height: 5,
            borderRadius: 999,
            flexShrink: 0,
            background: hue,
            boxShadow: done ? 'none' : `0 0 5px ${mix(53)}`,
          }}
        />
        <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span
            style={{
              fontSize: 12,
              color: done ? 'var(--hf-ink-3)' : 'var(--hf-ink)',
              textDecoration: done ? 'line-through' : 'none',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {name}
            {done && <span className="hf-sr-only"> (finished)</span>}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <ProjectID id={code} />
            {flag && (
              <span
                style={{
                  fontSize: 'var(--hf-text-2xs)',
                  lineHeight: 1.2,
                  padding: '1px 5px',
                  borderRadius: 3,
                  fontFamily: 'var(--hf-font-mono)',
                  ...FLAG[flag],
                }}
              >
                {flag}
              </span>
            )}
          </span>
        </span>
      </span>
      <span style={{ position: 'relative', display: 'block', height: 38 }}>
        {background}
        {bar ? (
          <span
            style={{
              position: 'absolute',
              top: 6,
              bottom: 6,
              left: `${Math.min(bar.from, 1 - width) * 100}%`,
              width: `${width * 100}%`,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              paddingLeft: 8,
              overflow: 'hidden',
              borderRadius: 4,
              background: mix(done ? 19 : 25),
              border: `1px solid ${mix(done ? 40 : 73)}`,
              boxShadow: done ? 'none' : `0 0 12px -4px ${mix(53)}`,
              filter: hot ? 'brightness(1.3)' : 'none',
              transition: 'filter 120ms',
            }}
          >
            {lead && <Avatar name={lead} size={18} />}
            {lead && width > 0.12 && (
              <span
                style={{
                  fontSize: 'var(--hf-text-2xs)',
                  fontFamily: 'var(--hf-font-mono)',
                  color: done ? 'var(--hf-ink-3)' : 'var(--hf-ink-2)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {lead.split(' ')[0]}
              </span>
            )}
          </span>
        ) : (
          note && (
            <span
              style={{
                position: 'absolute',
                top: '50%',
                left: 0,
                transform: 'translateY(-50%)',
                padding: '2px 8px',
                borderRadius: 4,
                border: '1px dashed var(--hf-rule-control)',
                background: 'var(--hf-surface-2)',
                fontSize: 11,
                color: 'var(--hf-ink-2)',
                whiteSpace: 'nowrap',
              }}
            >
              {note}
            </span>
          )
        )}
        {markers.map((m, i) => (
          <span
            key={i}
            role="img"
            aria-label={m.label}
            title={m.label}
            style={{
              position: 'absolute',
              top: 1,
              left: `${m.at * 100}%`,
              transform: 'translateX(-50%)',
              lineHeight: 0,
            }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
              <polygon
                points="5,0 10,5 5,10 0,5"
                fill={hue}
                stroke="var(--hf-ink)"
                strokeWidth="0.75"
              />
            </svg>
          </span>
        ))}
      </span>
    </>
  );
  const hover = { onMouseEnter: () => setHot(true), onMouseLeave: () => setHot(false) };
  return href ? (
    <Link to={href} style={style} {...hover}>
      {body}
    </Link>
  ) : (
    <div style={style}>{body}</div>
  );
};

// ── This week's strip ─────────────────────────────────────────────────────────
// Seven days in the caller's order, which must be the org's week, with
// `labels` from weekdayLabels(weekStartsOn) drawn under each day.
//
// It spoke a vocabulary of its own: present wore the public-holiday blue, a
// holiday the regional coral, leave the optional lilac, and "no record" an
// outline, the calendar's "waiting for HR"; nothing keyed it, so a day meant
// what its hover title said (critique 2026-10-06 #2). Owner's call,
// 2026-10-08: what only this strip says is a tall bar in a status colour,
// present mint, from home sky, absent rose, and an empty well where a working
// day has no record. Leave, a holiday and a day off are the calendars' own
// marks (Day marks, monthGrid.tsx): the leave bar in its type's colour, the
// square of the holiday's kind, the hatch. A short well is a day still ahead.
// The well is filled, never edged (an edge is "waiting for HR"), in a
// Progress track's surface-3. That is under 3:1 (1.17:1 on the record's header
// in light): the owner chose the faint well over the control-edge grey, which
// read heavy beside mint (2026-10-09), and UI-CONVENTIONS Day marks records it
// as the one accepted exception to "every mark ≥3:1". The day's words still
// say "no record" to a screen reader, and the key names it.
// The key under it names what the week holds, and role="img" with a sentence
// carries the week for a screen reader, the whole day name in it: read aloud,
// "We no record" is a sentence of its own and "Sa" is noise.
/** A day on the strip: an attendance status ('present', 'wfh', 'absent', or
 *  'upcoming' for a working day still ahead); a day of leave, by its type; a
 *  holiday, by its kind; or null for a day with no record. */
export type StripDay =
  string | { leave: string; color?: string } | { holiday: string; kind?: HolidayKind } | null;

// The strip's own marks.
const WELL = 'var(--hf-surface-3)';
const STATUS: Record<string, { text: string; key: string; fill: string; short?: true }> = {
  present: { text: 'present', key: 'Present', fill: markColor('sage') },
  wfh: { text: 'working from home', key: 'Working from home', fill: markColor('sky') },
  absent: { text: 'absent', key: 'Absent', fill: markColor('rose') },
  none: { text: 'no record', key: 'No record', fill: WELL },
  upcoming: { text: 'upcoming', key: 'Upcoming', fill: WELL, short: true },
};
const statusMark = (s: string): CSSProperties => ({
  display: 'inline-block',
  flexShrink: 0,
  width: 'var(--hf-space-2)',
  // A day ahead is a stub on the baseline, under the 8px a hatch or a square
  // takes, so the same grey can't read as a day off.
  height: STATUS[s]?.short ? 'var(--hf-space-1)' : 'var(--hf-space-4)',
  borderRadius: 'var(--hf-radius-xs)',
  background: STATUS[s]?.fill ?? WELL,
});

const WEEKDAY_NAME: Record<string, string> = {
  Su: 'Sunday',
  Mo: 'Monday',
  Tu: 'Tuesday',
  We: 'Wednesday',
  Th: 'Thursday',
  Fr: 'Friday',
  Sa: 'Saturday',
};

/** One day's mark, its words, and its state (for the key, and the tests). */
const stripMark = (
  d: StripDay,
  workday?: boolean,
): { state: string; style: CSSProperties; text: string } => {
  if (d && typeof d === 'object') {
    if ('leave' in d)
      return { state: 'leave', style: dayMarkStyle('leave', d.color), text: `${d.leave} leave` };
    return d.kind
      ? { state: d.kind, style: dayMarkStyle(d.kind), text: `${d.holiday}, ${d.kind} holiday` }
      : { state: 'holiday', style: dayMarkStyle('holiday'), text: `${d.holiday}, holiday` };
  }
  // A status row with nothing to say which leave or which holiday.
  if (d === 'leave') return { state: 'leave', style: dayMarkStyle('leave'), text: 'on leave' };
  if (d === 'holiday') return { state: 'holiday', style: dayMarkStyle('holiday'), text: 'holiday' };
  if (d == null && workday === false)
    return { state: 'off', style: dayMarkStyle('off'), text: 'non-working day' };
  const s = d ?? 'none';
  return { state: s, style: statusMark(s), text: STATUS[s]?.text ?? s };
};

export const AttendanceStrip = ({
  days = [],
  labels,
  workdays,
}: {
  days?: StripDay[];
  labels?: string[];
  /** Per position: is it a working day. A day with no record on one that
   *  isn't is hatched, not an empty well. */
  workdays?: boolean[];
}) => {
  const marks = days.map((d, i) => stripMark(d, workdays?.[i]));
  const day = (i: number) =>
    labels?.[i] ? (WEEKDAY_NAME[labels[i]] ?? labels[i]) : `Day ${i + 1}`;
  const summary = marks.map((m, i) => `${day(i)} ${m.text}`).join(', ');
  return (
    // No key under it (owner, 2026-10-09: "no need for the legend"): each
    // day names itself in its title, and the whole week in the label.
    <>
      <div role="img" aria-label={summary} style={{ display: 'flex', gap: 'var(--hf-space-1)' }}>
        {marks.map((m, i) => (
          <div
            key={i}
            title={labels?.[i] ? `${day(i)} · ${m.text}` : m.text}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--hf-space-1)',
            }}
          >
            {/* Every mark stands on one baseline in a 16px box: a stub is a
                day ahead, a square or a leave bar the calendars' mark. */}
            <span
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                height: 'var(--hf-space-4)',
              }}
            >
              <span data-state={m.state} style={m.style} />
            </span>
            {labels?.[i] && (
              <span style={{ fontSize: 'var(--hf-text-2xs)', color: 'var(--hf-ink-3)' }}>
                {labels[i]}
              </span>
            )}
          </div>
        ))}
      </div>
    </>
  );
};
